import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminOverviewPage.css";

type ReadinessCheck={
  key:string;
  label:string;
  status:"PASS"|"ATTENTION"|"BLOCKED"|string;
  detail:string;
};

type SchoolOperation={
  schoolId:string;
  schoolName:string;
  learnerCount:number;
  staffCount:number;
  openProgramCount:number;
  submittedEvidenceCount:number;
  draftEvaluationCount:number;
  candidateAchievementCount:number;
  credentialRequestCount:number;
  assuranceWorkCount:number;
};

type Overview={
  metrics:{
    activeOrganizations:number;
    activeSchools:number;
    activeAccounts:number;
    activeLearners:number;
    publishedAssessments:number;
    openPrograms:number;
    submittedEvidence:number;
    draftEvaluations:number;
    candidateAchievements:number;
    activeCredentialRequests:number;
    activeCredentials:number;
    verifiedAssuranceSources:number;
    verifiedAssuranceRules:number;
  };
  readiness:{
    status:"READY"|"ATTENTION"|"BLOCKED"|string;
    checks:ReadinessCheck[];
  };
  schools:SchoolOperation[];
};

function readinessLabel(value:string){
  if(value==="READY") return "정상 운영";
  if(value==="ATTENTION") return "확인 필요";
  if(value==="BLOCKED") return "즉시 조치";
  return value;
}

function checkLabel(value:string){
  if(value==="PASS") return "정상";
  if(value==="ATTENTION") return "확인";
  if(value==="BLOCKED") return "차단";
  return value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function schoolBacklog(school:SchoolOperation){
  return school.submittedEvidenceCount
    + school.draftEvaluationCount
    + school.candidateAchievementCount
    + school.credentialRequestCount
    + school.assuranceWorkCount;
}

function number(value:number){
  return value.toLocaleString("ko-KR");
}

export function AdminOverviewPage({client}:{client:PlatformClient}){
  const [data,setData]=useState<Overview|null>(null);
  const [error,setError]=useState("");
  const [query,setQuery]=useState("");
  const [schoolView,setSchoolView]=useState<"ALL"|"ATTENTION">("ALL");

  useEffect(()=>{
    let active=true;
    client.rpc("admin_platform_overview").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setData(data as Overview);
    });
    return()=>{active=false;};
  },[client]);

  const visibleSchools=useMemo(()=>{
    const source=[...(data?.schools || [])].sort((a,b)=>schoolBacklog(b)-schoolBacklog(a));
    const keyword=query.trim().toLowerCase();
    return source.filter((school)=>{
      if(schoolView==="ATTENTION" && schoolBacklog(school)===0) return false;
      return !keyword || school.schoolName.toLowerCase().includes(keyword);
    });
  },[data,query,schoolView]);

  if(error) return <p className="error">{error}</p>;
  if(!data) return <p className="muted">플랫폼 운영 현황을 불러오는 중…</p>;

  const pendingHumanReview=
    data.metrics.submittedEvidence+
    data.metrics.draftEvaluations+
    data.metrics.candidateAchievements+
    data.metrics.activeCredentialRequests;

  const attentionChecks=data.readiness.checks.filter((item)=>item.status!=="PASS");
  const schoolsNeedingAttention=data.schools.filter((school)=>schoolBacklog(school)>0).length;

  const pipelines=[
    {
      label:"증빙 검토",
      value:data.metrics.submittedEvidence,
      description:"제출된 활동 증빙 확인",
      path:"/assurance"
    },
    {
      label:"평가 확정",
      value:data.metrics.draftEvaluations,
      description:"사람의 최종 판단 대기",
      path:"/assurance"
    },
    {
      label:"성취 후보",
      value:data.metrics.candidateAchievements,
      description:"기준 충족 후보 검토",
      path:"/assurance"
    },
    {
      label:"발급 진행",
      value:data.metrics.activeCredentialRequests,
      description:"디지털 자격증명 발급 큐",
      path:"/credentials"
    }
  ];

  const workspaces=[
    {
      eyebrow:"REGIONAL GOVERNANCE",
      label:"지역·학교 관제",
      description:"학교별 지원 신호·운영률·보고·배지·검사",
      path:"/control-tower",
      value:data.metrics.activeSchools+"개 학교"
    },
    {
      eyebrow:"ROLLOUT",
      label:"학교 개통",
      description:"학교 일괄등록·담당교사 초대·개통 추적",
      path:"/school-rollout",
      value:"BATCH"
    },
    {
      eyebrow:"DATA MIGRATION",
      label:"데이터 이관",
      description:"학생명단·학급매핑·전학·학년이동 관제",
      path:"/data-migration",
      value:"IMPORT"
    },
    {
      eyebrow:"ANALYTICS",
      label:"성과 분석",
      description:"KPI·월별추이·학교비교·Executive Snapshot",
      path:"/analytics",
      value:"KPI"
    },
    {
      eyebrow:"PRIVACY",
      label:"개인정보 거버넌스",
      description:"보호자 동의·최소정보 공유·보존정책·Audit",
      path:"/privacy-governance",
      value:"CONSENT"
    },
    {
      eyebrow:"COMMUNICATIONS",
      label:"커뮤니케이션",
      description:"상태기반 학교 알림·Campaign·발송 Queue",
      path:"/communications",
      value:"NOTIFY"
    },
    {
      eyebrow:"SERVICE DESK",
      label:"서비스 데스크",
      description:"학교 문의·장애·SLA·반복원인 분석",
      path:"/service-desk",
      value:"SLA"
    },
    {
      eyebrow:"KNOWLEDGE",
      label:"지식센터",
      description:"FAQ·오류코드·Guided Support·티켓전환 분석",
      path:"/knowledge-base",
      value:"GUIDE"
    },
    {
      eyebrow:"LICENSE",
      label:"계약·라이선스",
      description:"계약기간·좌석·기능·검사·배지 Entitlement",
      path:"/contracts",
      value:"TENANT"
    },
    {
      eyebrow:"BILLING",
      label:"견적·청구",
      description:"견적·청구·입금·증빙·갱신 파이프라인",
      path:"/billing",
      value:"REVENUE"
    },
    {
      eyebrow:"ORGANIZATION",
      label:"기관·학교",
      description:"교육청·기관·학교 구조와 상태",
      path:"/organizations",
      value:data.metrics.activeOrganizations+"개 조직"
    },
    {
      eyebrow:"IDENTITY & ACCESS",
      label:"사용자·권한",
      description:"계정·소속·역할·접근 범위 관리",
      path:"/access",
      value:data.metrics.activeAccounts+"개 계정"
    },
    {
      eyebrow:"ASSESSMENT",
      label:"검사 스튜디오",
      description:"검사도구·문항·버전·발행 관리",
      path:"/assessment-studio",
      value:data.metrics.publishedAssessments+"개 발행"
    },
    {
      eyebrow:"COMPETENCY",
      label:"역량 체계",
      description:"역량·성취 기준과 연결 구조",
      path:"/competencies",
      value:"MASTER DATA"
    },
    {
      eyebrow:"CREDENTIAL",
      label:"발급 운영",
      description:"요청·발급·상태·검증 흐름",
      path:"/credentials",
      value:data.metrics.activeCredentials+"개 활성"
    },
    {
      eyebrow:"ASSURANCE",
      label:"정책·근거",
      description:"근거 출처·규칙·적용·검토",
      path:"/assurance",
      value:data.metrics.verifiedAssuranceRules+"개 규칙"
    },
    {
      eyebrow:"SYSTEM OPS",
      label:"시스템 운영",
      description:"Worker·Queue·Job·재시도 관제",
      path:"/system-operations",
      value:"JOBS"
    },
    {
      eyebrow:"AUDIT",
      label:"감사 추적",
      description:"보안 Finding·상관관계 이력",
      path:"/audit",
      value:"TRACE"
    },
    {
      eyebrow:"PLATFORM CONFIG",
      label:"플랫폼 설정",
      description:"OAuth·도메인·Worker·Issuer 연동",
      path:"/platform-configuration",
      value:"READINESS"
    }
  ];

  return (
    <section className="admin-command-overview">
      <header className="admin-command-hero">
        <div className="admin-command-copy">
          <p className="eyebrow">RS CAREER ERP · PLATFORM COMMAND CENTER</p>
          <h2>관리자 운영센터</h2>
          <p>
            기관·학교·사용자·검사·배지·정책·감사 흐름을 하나의 운영 화면에서 보고,
            지금 확인해야 할 일부터 바로 처리합니다.
          </p>

          <div className="admin-command-summary">
            <span><b>{number(data.metrics.activeLearners)}</b> 학생</span>
            <span><b>{number(data.metrics.activeAccounts)}</b> 계정</span>
            <span><b>{number(data.metrics.activeSchools)}</b> 학교</span>
            <span><b>{number(pendingHumanReview)}</b> 검토 대기</span>
          </div>
        </div>

        <button
          className={"admin-command-status "+data.readiness.status.toLowerCase()}
          onClick={()=>attentionChecks.length && document.getElementById("admin-readiness")?.scrollIntoView({behavior:"smooth"})}
        >
          <span>PLATFORM STATUS</span>
          <strong>{readinessLabel(data.readiness.status)}</strong>
          <small>
            {attentionChecks.length
              ? attentionChecks.length+"개 항목 확인 필요"
              : "핵심 운영 점검 통과"}
          </small>
        </button>
      </header>

      <section className="admin-command-section">
        <div className="admin-command-section-head">
          <div>
            <p className="eyebrow">TODAY'S OPERATIONS</p>
            <h3>지금 처리할 일</h3>
          </div>
          <span>{number(pendingHumanReview)}건 대기</span>
        </div>

        <div className="admin-today-grid">
          {pipelines.map((item)=>(
            <button key={item.label} className={item.value>0 ? "needs-action" : ""} onClick={()=>navigate(item.path)}>
              <span className="admin-today-label">{item.label}</span>
              <strong>{number(item.value)}</strong>
              <small>{item.description}</small>
              <b>{item.value>0 ? "처리하기 →" : "대기 없음"}</b>
            </button>
          ))}
        </div>
      </section>

      <section className="admin-command-section">
        <div className="admin-command-section-head">
          <div>
            <p className="eyebrow">ADMIN WORKSPACES</p>
            <h3>관리 영역</h3>
          </div>
          <span>19개 핵심 영역</span>
        </div>

        <div className="admin-workspace-grid">
          {workspaces.map((item)=>(
            <button key={item.label} onClick={()=>navigate(item.path)}>
              <span className="admin-workspace-eyebrow">{item.eyebrow}</span>
              <strong>{item.label}</strong>
              <p>{item.description}</p>
              <small>{item.value}</small>
            </button>
          ))}
        </div>
      </section>

      <div className="admin-command-two-column">
        <section className="admin-command-panel" id="admin-readiness">
          <div className="admin-command-section-head compact">
            <div>
              <p className="eyebrow">READINESS</p>
              <h3>운영 안전장치</h3>
            </div>
            <span>{data.readiness.checks.length} checks</span>
          </div>

          <div className="admin-readiness-list">
            {data.readiness.checks.map((item)=>(
              <div className="admin-readiness-row" key={item.key}>
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </div>
                <span className={"readiness-chip "+item.status.toLowerCase()}>
                  {checkLabel(item.status)}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-command-panel">
          <div className="admin-command-section-head compact">
            <div>
              <p className="eyebrow">SERVICE SCALE</p>
              <h3>현재 운영 규모</h3>
            </div>
            <span>{schoolsNeedingAttention}개 학교 작업 존재</span>
          </div>

          <div className="admin-scale-grid">
            <Scale label="운영 중 프로그램" value={data.metrics.openPrograms} />
            <Scale label="발행 검사" value={data.metrics.publishedAssessments} />
            <Scale label="활성 자격증명" value={data.metrics.activeCredentials} />
            <Scale label="검증 근거 Source" value={data.metrics.verifiedAssuranceSources} />
            <Scale label="검증 정책 Rule" value={data.metrics.verifiedAssuranceRules} />
            <Scale label="활성 기관" value={data.metrics.activeOrganizations} />
          </div>
        </section>
      </div>

      <section className="admin-command-panel admin-school-operations">
        <div className="admin-command-section-head">
          <div>
            <p className="eyebrow">SCHOOL OPERATIONS</p>
            <h3>학교별 운영 현황</h3>
          </div>
          <div className="admin-school-tools">
            <div className="admin-school-segment">
              <button className={schoolView==="ALL" ? "active" : ""} onClick={()=>setSchoolView("ALL")}>전체</button>
              <button className={schoolView==="ATTENTION" ? "active" : ""} onClick={()=>setSchoolView("ATTENTION")}>작업 있음</button>
            </div>
            <input
              value={query}
              onChange={(event)=>setQuery(event.target.value)}
              placeholder="학교명 검색"
              aria-label="학교명 검색"
            />
          </div>
        </div>

        <div className="school-ops-table upgraded">
          <div className="school-ops-head">
            <span>학교</span>
            <span>학생</span>
            <span>교직원</span>
            <span>프로그램</span>
            <span>증빙</span>
            <span>평가</span>
            <span>성취</span>
            <span>발급</span>
            <span>전체 작업</span>
          </div>

          {visibleSchools.map((school)=>{
            const backlog=schoolBacklog(school);
            return (
              <div className="school-ops-row" key={school.schoolId}>
                <span className="school-ops-name">
                  <strong>{school.schoolName}</strong>
                  <small>{backlog>0 ? "확인할 작업 "+backlog+"건" : "운영 정상"}</small>
                </span>
                <span>{number(school.learnerCount)}</span>
                <span>{number(school.staffCount)}</span>
                <span>{number(school.openProgramCount)}</span>
                <span>{number(school.submittedEvidenceCount)}</span>
                <span>{number(school.draftEvaluationCount)}</span>
                <span>{number(school.candidateAchievementCount)}</span>
                <span>{number(school.credentialRequestCount)}</span>
                <span className={backlog>0 ? "school-backlog" : "school-clear"}>{number(backlog)}</span>
              </div>
            );
          })}

          {!visibleSchools.length && (
            <div className="admin-school-empty">
              조건에 맞는 학교가 없습니다.
            </div>
          )}
        </div>
      </section>
    </section>
  );
}

function Scale({label,value}:{label:string;value:number}){
  return (
    <div className="admin-scale-card">
      <span>{label}</span>
      <strong>{number(value)}</strong>
    </div>
  );
}
